import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Key,
  Plus,
  Trash2,
  Copy,
  Clock,
  Shield,
  ChevronDown,
  ChevronUp,
  Activity,
  Users,
  BarChart3,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────

interface ApiKeyInfo {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

interface IngestedContact {
  id: string;
  external_id: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  status: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  lifetime_value: string | null;
  currency: string | null;
  custom_data: Record<string, unknown> | null;
  updated_at: string;
  created_at: string;
}

interface FunnelStep {
  event_name: string;
  count: number;
  unique_users: number;
}

interface RecentEvent {
  id: string;
  event_name: string;
  email: string | null;
  external_id: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  revenue: string | null;
  currency: string | null;
  properties: Record<string, unknown> | null;
  event_timestamp: string;
}

// ─── Status badge helper ─────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  converted: "bg-green-600/20 text-green-400",
  trial: "bg-blue-600/20 text-blue-400",
  churned: "bg-red-600/20 text-red-400",
  lead: "bg-white/10 text-white/40",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge className={`text-[9px] ${STATUS_STYLES[status] || STATUS_STYLES.lead}`}>{status}</Badge>
  );
}

// ─── Expandable contact row ──────────────────────────────

function ContactRow({ contact }: { contact: IngestedContact }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "pl" ? "pl-PL" : "en-US";
  const [expanded, setExpanded] = useState(false);

  const allParams: [string, string | null][] = [
    [t("clientApi.field.email"), contact.email],
    [t("clientApi.field.firstName"), contact.first_name],
    [t("clientApi.field.lastName"), contact.last_name],
    [t("clientApi.field.phone"), contact.phone],
    [t("clientApi.field.externalId"), contact.external_id],
    [t("clientApi.field.status"), contact.status],
    [t("clientApi.field.utmSource"), contact.utm_source],
    [t("clientApi.field.utmMedium"), contact.utm_medium],
    [t("clientApi.field.utmCampaign"), contact.utm_campaign],
    [t("clientApi.field.utmContent"), contact.utm_content],
    [t("clientApi.field.utmTerm"), contact.utm_term],
    [
      t("clientApi.field.ltv"),
      contact.lifetime_value ? `${contact.lifetime_value} ${contact.currency || "PLN"}` : null,
    ],
    [
      t("clientApi.field.created"),
      contact.created_at ? new Date(contact.created_at).toLocaleString(locale) : null,
    ],
    [
      t("clientApi.field.updated"),
      contact.updated_at ? new Date(contact.updated_at).toLocaleString(locale) : null,
    ],
  ];

  const customDataEntries = contact.custom_data ? Object.entries(contact.custom_data) : [];

  return (
    <>
      <tr
        className="text-white/60 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="p-1.5">{contact.email || "—"}</td>
        <td className="p-1.5">
          {[contact.first_name, contact.last_name].filter(Boolean).join(" ") || "—"}
        </td>
        <td className="p-1.5">
          <StatusBadge status={contact.status} />
        </td>
        <td className="p-1.5">{contact.utm_source || "—"}</td>
        <td className="p-1.5">{contact.utm_campaign || "—"}</td>
        <td className="p-1.5">{new Date(contact.updated_at).toLocaleDateString(locale)}</td>
        <td className="p-1.5 text-center">
          {expanded ? (
            <ChevronUp className="w-3 h-3 inline" />
          ) : (
            <ChevronDown className="w-3 h-3 inline" />
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-white/5">
          <td colSpan={7} className="p-0">
            <div className="bg-white/[0.03] p-3 space-y-1">
              <p className="text-[10px] text-white/60 font-medium mb-2">
                {t("clientApi.allParams")}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                {allParams.map(([label, value]) => (
                  <div key={label} className="flex gap-1 text-[10px]">
                    <span className="text-white/40">{label}:</span>
                    <span className="text-white/70 break-all">{value || "—"}</span>
                  </div>
                ))}
              </div>
              {customDataEntries.length > 0 && (
                <div className="mt-2 border-t border-white/10 pt-2">
                  <p className="text-[10px] text-orange-400/80 font-medium mb-1">
                    {t("clientApi.customData")}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                    {customDataEntries.map(([key, val]) => (
                      <div key={key} className="flex gap-1 text-[10px]">
                        <span className="text-white/40">{key}:</span>
                        <span className="text-white/70 break-all">{String(val)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Expandable event row ────────────────────────────────

function EventRow({ event }: { event: RecentEvent }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "pl" ? "pl-PL" : "en-US";
  const [expanded, setExpanded] = useState(false);

  const allParams: [string, string | null][] = [
    [t("clientApi.field.event"), event.event_name],
    [t("clientApi.field.email"), event.email],
    [t("clientApi.field.externalId"), event.external_id],
    [t("clientApi.field.utmSource"), event.utm_source],
    [t("clientApi.field.utmMedium"), event.utm_medium],
    [t("clientApi.field.utmCampaign"), event.utm_campaign],
    [t("clientApi.field.utmContent"), event.utm_content],
    [t("clientApi.field.utmTerm"), event.utm_term],
    [
      t("clientApi.field.revenue"),
      event.revenue ? `${event.revenue} ${event.currency || "PLN"}` : null,
    ],
    [
      t("clientApi.field.timestamp"),
      event.event_timestamp ? new Date(event.event_timestamp).toLocaleString(locale) : null,
    ],
  ];

  const propertiesEntries = event.properties ? Object.entries(event.properties) : [];

  return (
    <>
      <tr
        className="text-white/60 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="p-1.5">
          <Badge className="text-[9px] bg-purple-600/20 text-purple-400">{event.event_name}</Badge>
        </td>
        <td className="p-1.5">{event.email || "—"}</td>
        <td className="p-1.5">{event.utm_source || "—"}</td>
        <td className="p-1.5">{event.utm_campaign || "—"}</td>
        <td className="p-1.5">
          {event.revenue ? `${event.revenue} ${event.currency || "PLN"}` : "—"}
        </td>
        <td className="p-1.5">{new Date(event.event_timestamp).toLocaleString(locale)}</td>
        <td className="p-1.5 text-center">
          {expanded ? (
            <ChevronUp className="w-3 h-3 inline" />
          ) : (
            <ChevronDown className="w-3 h-3 inline" />
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-white/5">
          <td colSpan={7} className="p-0">
            <div className="bg-white/[0.03] p-3 space-y-1">
              <p className="text-[10px] text-white/60 font-medium mb-2">
                {t("clientApi.allParams")}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                {allParams.map(([label, value]) => (
                  <div key={label} className="flex gap-1 text-[10px]">
                    <span className="text-white/40">{label}:</span>
                    <span className="text-white/70 break-all">{value || "—"}</span>
                  </div>
                ))}
              </div>
              {propertiesEntries.length > 0 && (
                <div className="mt-2 border-t border-white/10 pt-2">
                  <p className="text-[10px] text-orange-400/80 font-medium mb-1">
                    {t("clientApi.properties")}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                    {propertiesEntries.map(([key, val]) => (
                      <div key={key} className="flex gap-1 text-[10px]">
                        <span className="text-white/40">{key}:</span>
                        <span className="text-white/70 break-all">{String(val)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main panel ──────────────────────────────────────────

export function ClientApiPanel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "pl" ? "pl-PL" : "en-US";
  const { toast } = useToast();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState<number | null>(null);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [showContacts, setShowContacts] = useState(false);
  const [showEvents, setShowEvents] = useState(false);

  const { data: keysData, isLoading } = useQuery<{ keys: ApiKeyInfo[] }>({
    queryKey: ["/api/external/keys"],
  });

  const { data: contactsData } = useQuery<{
    contacts: IngestedContact[];
    pagination: { total: number };
  }>({
    queryKey: ["/api/external/keys/contacts"],
    enabled: showContacts,
  });

  const { data: funnelData } = useQuery<{ funnel: FunnelStep[] }>({
    queryKey: ["/api/external/keys/events/funnel"],
    enabled: showEvents,
  });

  const { data: recentEventsData } = useQuery<{ events: RecentEvent[] }>({
    queryKey: ["/api/external/keys/events/recent"],
    enabled: showEvents,
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; expires_in_days?: number }) => {
      return await apiRequest("POST", "/api/external/keys", data);
    },
    onSuccess: (data: { api_key: string; key_info: ApiKeyInfo }) => {
      setGeneratedKey(data.api_key);
      setShowCreateForm(false);
      setNewKeyName("");
      setExpiresInDays(null);
      queryClient.invalidateQueries({ queryKey: ["/api/external/keys"] });
      toast({ title: t("clientApi.toast.created"), description: t("clientApi.toast.createdDesc") });
    },
    onError: (error: Error) => {
      toast({
        title: t("clientApi.toast.tryAgain"),
        description: t("clientApi.toast.tryAgainDesc"),
        variant: "destructive",
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/external/keys/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/external/keys"] });
      toast({ title: t("clientApi.toast.revoked") });
    },
    onError: (error: Error) => {
      toast({
        title: t("clientApi.toast.tryAgain"),
        description: t("clientApi.toast.tryAgainDesc"),
        variant: "destructive",
      });
    },
  });

  const activeKeys = keysData?.keys?.filter((k) => !k.revoked_at) || [];
  const revokedKeys = keysData?.keys?.filter((k) => k.revoked_at) || [];

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: t("clientApi.toast.copied") });
  };

  const funnelSteps = funnelData?.funnel || [];
  const maxCount = Math.max(...funnelSteps.map((f) => f.count), 1);

  return (
    <div className="space-y-4">
      {/* Header with connection info */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-white flex items-center gap-2">
            <Key className="w-4 h-4 text-orange-400" />
            {t("clientApi.title")}
          </h3>
          <p className="text-[10px] text-white/40 mt-0.5">{t("clientApi.subtitle")}</p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            setGeneratedKey(null);
          }}
          className="bg-orange-600 hover:bg-orange-700 text-white text-xs"
        >
          <Plus className="w-3 h-3 mr-1" />
          {t("clientApi.newKey")}
        </Button>
      </div>

      {/* Generated key banner */}
      {generatedKey && (
        <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-green-400" />
            <span className="text-green-400 text-sm font-medium">
              {t("clientApi.newKeyBanner")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-black/50 text-green-300 text-xs px-3 py-2 rounded font-mono break-all">
              {generatedKey}
            </code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => copyToClipboard(generatedKey)}
              className="border-green-500/50 text-green-400 hover:bg-green-900/50"
            >
              <Copy className="w-3 h-3" />
            </Button>
          </div>
          <p className="text-green-400/60 text-[10px] mt-2">{t("clientApi.newKeyHint")}</p>
        </div>
      )}

      {/* Create form */}
      {showCreateForm && (
        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
          <div className="space-y-3">
            <div>
              <label className="text-white/50 text-xs mb-1 block">{t("clientApi.form.name")}</label>
              <Input
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder={t("clientApi.form.namePlaceholder")}
                className="bg-black/50 border-white/20 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-white/50 text-xs mb-1 block">
                {t("clientApi.form.expires")}
              </label>
              <Input
                type="number"
                value={expiresInDays ?? ""}
                onChange={(e) => setExpiresInDays(e.target.value ? Number(e.target.value) : null)}
                placeholder={t("clientApi.form.expiresPlaceholder")}
                className="bg-black/50 border-white/20 text-white text-sm"
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() =>
                  createMutation.mutate({
                    name: newKeyName,
                    ...(expiresInDays ? { expires_in_days: expiresInDays } : {}),
                  })
                }
                disabled={!newKeyName.trim() || createMutation.isPending}
                className="bg-orange-600 hover:bg-orange-700 text-white text-xs"
              >
                {createMutation.isPending
                  ? t("clientApi.form.creating")
                  : t("clientApi.form.create")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowCreateForm(false)}
                className="text-white/50 text-xs"
              >
                {t("clientApi.form.cancel")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* API usage instructions */}
      <div className="bg-white/5 rounded-lg p-3 border border-white/10">
        <p className="text-white/60 text-xs font-medium mb-2">{t("clientApi.examplesTitle")}</p>
        <code className="text-[10px] text-white/40 block whitespace-pre-wrap font-mono">
          {`# ${t("clientApi.example.contact")}
curl -X POST ${window.location.origin}/api/v1/contacts \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: gfy_..." \\
  -d '{"email":"jan@example.com","utm_source":"google","utm_campaign":"brand"}'

# ${t("clientApi.example.event")}
curl -X POST ${window.location.origin}/api/v1/events \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: gfy_..." \\
  -d '{"event_name":"registerSuccess","email":"jan@example.com","utm_source":"google","properties":{"plan":"pro"}}'

# ${t("clientApi.example.funnel")}
curl ${window.location.origin}/api/v1/events/funnel \\
  -H "X-API-Key: gfy_..."`}
        </code>
      </div>

      {/* Active keys list */}
      {isLoading ? (
        <p className="text-white/30 text-sm">{t("clientApi.loading")}</p>
      ) : activeKeys.length === 0 ? (
        <p className="text-white/30 text-sm">{t("clientApi.noKeys")}</p>
      ) : (
        <div className="space-y-2">
          {activeKeys.map((key) => (
            <div
              key={key.id}
              className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 border border-white/10"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-medium">{key.name}</span>
                  <Badge className="text-[10px] bg-green-600/20 text-green-400 border-green-500/50">
                    {t("clientApi.activeBadge")}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-white/30 text-[10px] font-mono">
                    gfy_{key.key_prefix}...
                  </span>
                  {key.last_used_at && (
                    <span className="text-white/30 text-[10px] flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {t("clientApi.lastUsed", {
                        date: new Date(key.last_used_at).toLocaleDateString(locale),
                      })}
                    </span>
                  )}
                  {key.expires_at && (
                    <span className="text-white/30 text-[10px]">
                      {t("clientApi.expires", {
                        date: new Date(key.expires_at).toLocaleDateString(locale),
                      })}
                    </span>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (confirm(t("clientApi.revokeConfirm"))) {
                    revokeMutation.mutate(key.id);
                  }
                }}
                className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {revokedKeys.length > 0 && (
        <div className="text-white/20 text-[10px]">
          {t("clientApi.revokedCount", { count: revokedKeys.length })}
        </div>
      )}

      {/* ─── Contacts section (expandable rows with all params) ─── */}
      <div className="border-t border-white/10 pt-3">
        <button
          onClick={() => setShowContacts(!showContacts)}
          className="flex items-center gap-2 text-white/60 text-xs hover:text-white/80 transition-colors"
        >
          <Users className="w-3.5 h-3.5" />
          {t("clientApi.contactsHeader", { total: contactsData?.pagination?.total ?? "..." })}
          {showContacts ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>

        {showContacts && contactsData && (
          <div className="mt-3">
            {contactsData.contacts.length === 0 ? (
              <p className="text-white/30 text-xs">{t("clientApi.noContacts")}</p>
            ) : (
              <div className="max-h-96 overflow-auto rounded-lg border border-white/10">
                <table className="w-full text-[10px]">
                  <thead className="sticky top-0 bg-[#0a0c10]">
                    <tr className="text-white/40 border-b border-white/10">
                      <th className="text-left p-1.5">{t("clientApi.field.email")}</th>
                      <th className="text-left p-1.5">{t("clientApi.field.firstName")}</th>
                      <th className="text-left p-1.5">{t("clientApi.field.status")}</th>
                      <th className="text-left p-1.5">{t("clientApi.field.utmSource")}</th>
                      <th className="text-left p-1.5">{t("clientApi.field.utmCampaign")}</th>
                      <th className="text-left p-1.5">{t("clientApi.field.updated")}</th>
                      <th className="text-center p-1.5 w-6"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactsData.contacts.map((c) => (
                      <ContactRow key={c.id} contact={c} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Funnel / Events section ─── */}
      <div className="border-t border-white/10 pt-3">
        <button
          onClick={() => setShowEvents(!showEvents)}
          className="flex items-center gap-2 text-white/60 text-xs hover:text-white/80 transition-colors"
        >
          <Activity className="w-3.5 h-3.5" />
          {t("clientApi.funnelHeader")}
          {showEvents ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>

        {showEvents && (
          <div className="mt-3 space-y-4">
            {/* Funnel bar chart */}
            {funnelSteps.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] text-white/50 font-medium flex items-center gap-1">
                  <BarChart3 className="w-3 h-3" />
                  {t("clientApi.funnelSummary")}
                </p>
                {funnelSteps.map((step) => (
                  <div key={step.event_name} className="flex items-center gap-2">
                    <span className="text-[10px] text-white/60 w-28 truncate">
                      {step.event_name}
                    </span>
                    <div className="flex-1 h-4 bg-white/5 rounded overflow-hidden">
                      <div
                        className="h-full bg-orange-500/40 rounded"
                        style={{ width: `${(step.count / maxCount) * 100}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-white/50 w-16 text-right">
                      {t("clientApi.funnelCount", { count: step.count, uniq: step.unique_users })}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Recent events with expandable rows */}
            {recentEventsData && recentEventsData.events.length > 0 && (
              <div>
                <p className="text-[10px] text-white/50 font-medium mb-2">
                  {t("clientApi.recentEvents")}
                </p>
                <div className="max-h-96 overflow-auto rounded-lg border border-white/10">
                  <table className="w-full text-[10px]">
                    <thead className="sticky top-0 bg-[#0a0c10]">
                      <tr className="text-white/40 border-b border-white/10">
                        <th className="text-left p-1.5">{t("clientApi.col.event")}</th>
                        <th className="text-left p-1.5">{t("clientApi.field.email")}</th>
                        <th className="text-left p-1.5">{t("clientApi.field.utmSource")}</th>
                        <th className="text-left p-1.5">{t("clientApi.field.utmCampaign")}</th>
                        <th className="text-left p-1.5">{t("clientApi.col.revenue")}</th>
                        <th className="text-left p-1.5">{t("clientApi.col.time")}</th>
                        <th className="text-center p-1.5 w-6"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentEventsData.events.map((e) => (
                        <EventRow key={e.id} event={e} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(!recentEventsData || recentEventsData.events.length === 0) &&
              funnelSteps.length === 0 && (
                <p className="text-white/30 text-xs">{t("clientApi.noEvents")}</p>
              )}
          </div>
        )}
      </div>
    </div>
  );
}
